import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Clock, CheckCircle2, XCircle, AlertTriangle, RefreshCw, Filter, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const statusStyle = {
  ok: "bg-emerald-100 text-emerald-800 border-emerald-300",
  accepted: "bg-blue-100 text-blue-800 border-blue-300",
  duplicate: "bg-slate-100 text-slate-700 border-slate-300",
  failed: "bg-red-100 text-red-800 border-red-300",
  not_configured: "bg-amber-100 text-amber-800 border-amber-300",
  unknown: "bg-slate-100 text-slate-700 border-slate-300",
};
const statusIcon = { ok: CheckCircle2, accepted: Clock, duplicate: Clock, failed: XCircle, not_configured: AlertTriangle };

export default function CronRuns() {
  const { user } = useAuth();
  const [runs, setRuns] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setRuns((await api.get("/cron/runs")).data); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    if (user && !["Admin", "Auditor"].includes(user.role)) return;
    load();
  }, [user]);

  if (user && !["Admin", "Auditor"].includes(user.role)) {
    return <Navigate to="/app" replace />;
  }

  const filtered = runs.filter(r => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (query.trim() && !`${r.name} ${r.run_id || ""}`.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  const counts = runs.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Operations · Crons</div>
          <h1 className="font-display text-3xl font-extrabold mt-1">Cron Runs</h1>
          <p className="text-sm text-slate-500 mt-1">Last 50 platform-scheduled webhook invocations. Filter by status to find broken syncs before month-end.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={async () => {
            try {
              const r = await api.post("/cron/sla-nag-preview", {});
              setPreview(r.data); setPreviewOpen(true);
            } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
          }} data-testid="cron-preview-btn"><Eye className="w-4 h-4 mr-1" /> Preview digest</Button>
          <Button variant="outline" onClick={load} data-testid="cron-refresh-btn"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
        <Stat label="OK" value={counts.ok || 0} tone="emerald" testId="cron-stat-ok" />
        <Stat label="Accepted" value={counts.accepted || 0} tone="blue" testId="cron-stat-accepted" />
        <Stat label="Duplicate" value={counts.duplicate || 0} tone="slate" testId="cron-stat-duplicate" />
        <Stat label="Not configured" value={counts.not_configured || 0} tone="amber" testId="cron-stat-notconf" />
        <Stat label="Failed" value={counts.failed || 0} tone="red" testId="cron-stat-failed" />
      </div>

      <Card className="mb-4">
        <CardContent className="p-3 flex items-center gap-3 flex-wrap">
          <Filter className="w-4 h-4 text-slate-500" />
          <Tabs value={statusFilter} onValueChange={setStatusFilter}>
            <TabsList data-testid="cron-status-tabs">
              <TabsTrigger value="all" data-testid="cron-tab-all">All</TabsTrigger>
              <TabsTrigger value="ok" data-testid="cron-tab-ok">OK</TabsTrigger>
              <TabsTrigger value="failed" data-testid="cron-tab-failed">Failed</TabsTrigger>
              <TabsTrigger value="not_configured" data-testid="cron-tab-notconf">Not configured</TabsTrigger>
            </TabsList>
          </Tabs>
          <Input placeholder="Search name or run_id…" className="max-w-xs" value={query} onChange={e => setQuery(e.target.value)} data-testid="cron-search" />
          <span className="ml-auto text-xs text-slate-500">{filtered.length} of {runs.length}</span>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 bg-slate-50">
              <tr className="border-b">
                <th className="text-left px-3 py-2">Cron</th>
                <th className="text-left px-3 py-2">Run ID</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Result</th>
                <th className="text-right px-3 py-2">At</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="text-center py-10 text-slate-500">Loading…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={5} className="text-center py-14 text-slate-500">
                  <Clock className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <div className="text-sm">No cron runs yet.</div>
                  <div className="text-xs mt-1">Crons kick off in preview immediately; deployed apps sync on next deploy.</div>
                </td></tr>
              )}
              {!loading && filtered.map(r => {
                const Icon = statusIcon[r.status] || Clock;
                return (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`cron-row-${r.name}`}>
                    <td className="px-3 py-3 font-mono text-xs font-semibold">{r.name}</td>
                    <td className="px-3 py-3 font-mono text-xs text-slate-500">{r.run_id || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-3">
                      <Badge variant="outline" className={`text-[10px] uppercase font-semibold border ${statusStyle[r.status] || statusStyle.unknown}`}>
                        <Icon className="w-3 h-3 mr-1 inline" /> {r.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-600">
                      {r.result ? <ResultSummary result={r.result} /> : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-3 text-right text-xs font-mono text-slate-500">{r.at ? new Date(r.at).toLocaleString() : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl" data-testid="cron-preview-dialog">
          <DialogHeader><DialogTitle>SLA-Nag Preview</DialogTitle></DialogHeader>
          {preview && (
            <>
              <div className="text-xs text-slate-600 mb-3">
                Would send digests to <b>{preview.would_send_to}</b> of {preview.approver_count} approvers. No emails were sent.
              </div>
              <div className="max-h-96 overflow-y-auto space-y-3">
                {preview.digests.length === 0 && <div className="text-sm text-slate-500 text-center py-6">Nobody is past SLA right now.</div>}
                {preview.digests.map(d => (
                  <div key={d.approver_id} className="border border-slate-200 rounded p-3" data-testid={`preview-digest-${d.approver_email || d.approver_id}`}>
                    <div className="flex items-center justify-between text-xs">
                      <div><b>{d.approver_name}</b> <span className="text-slate-500">· {d.role}</span> <span className="font-mono text-slate-500">{d.approver_email || "-"}</span></div>
                      <Badge className="bg-red-600 text-white">{d.overdue_count} overdue</Badge>
                    </div>
                    <table className="w-full mt-2 text-xs">
                      <tbody>
                        {d.items.map(it => (
                          <tr key={`${it.kind}-${it.reference}`} className="border-t border-slate-100">
                            <td className="py-1 font-mono">{it.reference}</td>
                            <td className="py-1 text-slate-500">{it.kind}</td>
                            <td className="py-1">{it.requester_name}</td>
                            <td className="py-1 text-right font-mono text-emerald-700">₹ {(it.amount || 0).toLocaleString()}</td>
                            <td className="py-1 text-right text-red-700 font-mono">{it.age_days}d / {it.sla_days}d</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ResultSummary({ result }) {
  if (result.error) return <span className="text-red-700 font-mono text-[11px]">{result.error.slice(0, 80)}</span>;
  const keys = ["fetched", "created", "updated", "sent", "skipped", "approvers", "total_items"];
  const parts = keys.filter(k => result[k] !== undefined).map(k => `${k}=${result[k]}`);
  return <span className="font-mono text-[11px]">{parts.join(" · ") || JSON.stringify(result).slice(0, 80)}</span>;
}

function Stat({ label, value, tone, testId }) {
  const cls = {emerald:"text-emerald-700",blue:"text-blue-700",red:"text-red-700",amber:"text-amber-700",slate:"text-slate-700"}[tone] || "text-slate-900";
  return (<Card><CardContent className="p-3" data-testid={testId}>
    <div className="text-[10px] uppercase text-slate-500 tracking-wider">{label}</div>
    <div className={`font-display font-bold text-2xl ${cls}`}>{value}</div>
  </CardContent></Card>);
}
